import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { getCustomerToken } from "../utils/CustomerAuth";
import { CartServices } from "../services/CartServices";
import { WishlistServices } from "../services/WishlistServices";

/* -------------------------
   PRICE + DISCOUNT HELPERS
-------------------------- */
const parseMoney = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;

  const s = String(v).trim();
  const cleaned = s.replace(/[^0-9.]/g, "");
  if (!cleaned) return null;

  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

const toMoneyText = (v) => {
  if (v === null || v === undefined) return "";
  return typeof v === "string" ? v : `$${Number(v).toFixed(2)}`;
};

const calcDiscountPercent = (regular, current) => {
  const r = parseMoney(regular);
  const c = parseMoney(current);
  if (!r || !c) return null;
  if (r <= 0) return null;
  if (c >= r) return null;

  const pct = Math.round(((r - c) / r) * 100);
  return pct > 0 ? pct : null;
};

const ProductCard = ({
  id,
  imageSrc,
  title,
  category = "Vegetables",

  oldPrice = "$28.99",
  newPrice = "$14.99",

  discount = null,
  badgeText = null,
  badgeType = null,

  inStock = true,
  noGrid = false,
  detailsUrl = id ? `/product-details/${id}` : "/product-details",

  // ✅ Real rating data from DB (passed from ShopSection via products useMemo)
  rating = null,       // number | null  — avg_rating from withAvg()
  reviewCount = 0,     // number         — review_count from withCount()

  // ✅ OPTIONAL: if product has selected variant in listing card
  locationProductPriceId = null,
  productInfoId = null,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [wishActive, setWishActive] = useState(false);
  const [togglingWish, setTogglingWish] = useState(false);
  const [adding, setAdding] = useState(false);

  // ✅ Build product URL with location filter preserved
  const productUrl = useMemo(() => {
    const baseUrl = id ? `/product-details/${id}` : "/product-details";
    const loc = searchParams.get("loc");
    
    if (loc) {
      return `${baseUrl}?loc=${encodeURIComponent(loc)}`;
    }
    
    return baseUrl;
  }, [id, searchParams]);

  // ✅ Auto discount from prices (only shows if regular > current)
  const autoDiscountPercent = useMemo(() => {
    return calcDiscountPercent(oldPrice, newPrice);
  }, [oldPrice, newPrice]);

  // ✅ Final discount badge text
  const discountBadgeText = useMemo(() => {
    if (discount !== null && discount !== undefined && String(discount).trim() !== "") {
      let text = String(discount).trim();
      text = text.replace(/\s*OFF\s*$/i, "").trim();

      const numeric = Number(text.replace("%", "").trim());
      if (Number.isFinite(numeric) && numeric > 0) return `${Math.round(numeric)}% OFF`;
      if (text.includes("%")) return `${text} OFF`;
      return text;
    }

    if (autoDiscountPercent) return `${autoDiscountPercent}% OFF`;
    return null;
  }, [discount, autoDiscountPercent]);

  // ✅ Resolved rating — coerce to number (DB subquery returns strings e.g. "4.3")
  const resolvedRating = (() => {
    if (rating === null || rating === undefined || rating === "") return null;
    const n = Number(rating);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const resolvedReviewCount = Number.isFinite(Number(reviewCount)) ? Number(reviewCount) : 0;
  const hasRating = resolvedRating !== null && resolvedReviewCount > 0;

  // ✅ StarDisplay — CSS overlay technique from ProductDetailsOne
  // Gray star as base, colored star clipped on top (supports exact half stars)
  const renderStars = (value, size = 14) => {
    const clamped = Math.max(0, Math.min(5, Number(value) || 0));
    return Array.from({ length: 5 }, (_, i) => {
      const filled = clamped >= i + 1;
      const half   = !filled && clamped > i && clamped < i + 1;
      return (
        <span
          key={i}
          style={{ position: "relative", display: "inline-block", fontSize: size, lineHeight: 1, color: "#D1D5DB" }}
        >
          <span style={{ color: "#D1D5DB" }}>★</span>
          {(filled || half) && (
            <span
              style={{
                position: "absolute",
                left: 0, top: 0,
                overflow: "hidden",
                width: filled ? "100%" : "50%",
                color: "#F59E0B",
              }}
            >
              ★
            </span>
          )}
        </span>
      );
    });
  };

  // ✅ Get current location_id from localStorage (same logic as cart)
  const getCurrentLocationId = () => {
    try {
      const selectedLocationName = localStorage.getItem("selectedLocation");
      if (!selectedLocationName) return null;

      const locationsList = JSON.parse(localStorage.getItem("locationsList") || "[]");
      const location = locationsList.find(
        (loc) => String(loc?.name || "").toLowerCase() === String(selectedLocationName || "").toLowerCase()
      );
      return location?.id ?? null;
    } catch {
      return null;
    }
  };

  // ✅ check wishlist state from API
  const loadWishState = async () => {
    const token = getCustomerToken();
    if (!token) { setWishActive(false); return; }

    try {
      const items = await WishlistServices.getWishlist();
      const exists = (items || []).some((w) => {
        const sameProduct = String(w?.product_id ?? w?.product?.id ?? "") === String(id);
        const wInfoId = w?.product_info_id ?? w?.productInfo?.id ?? null;
        const sameVariant =
          (productInfoId == null && (wInfoId == null || wInfoId === "")) ||
          String(wInfoId ?? "") === String(productInfoId ?? "");
        return sameProduct && sameVariant;
      });
      setWishActive(exists);
    } catch (e) {
      console.error("Failed to load wishlist state", e);
      setWishActive(false);
    }
  };

  useEffect(() => {
    loadWishState();
    const refresh = () => loadWishState();
    window.addEventListener("wishlist_updated", refresh);
    return () => window.removeEventListener("wishlist_updated", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, productInfoId]);

  // ✅ toggle wishlist via API
  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;

    const token = getCustomerToken();
    if (!token) { navigate("/login", { state: { from: productUrl } }); return; }

    try {
      setTogglingWish(true);
      
      // ✅ Build payload with location_product_price_id (same as addToCart)
      const payload = {
        product_id: id,
        product_info_id: productInfoId ?? null,
        location_id: getCurrentLocationId(), // ✅ Pass location for proper pricing
      };

      // ✅ If locationProductPriceId is available, pass it directly (same as cart)
      if (locationProductPriceId) {
        payload.location_product_price_id = locationProductPriceId;
      }
      const res = await WishlistServices.toggle(payload);

      if (typeof res?.is_wishlisted === "boolean") {
        setWishActive(res.is_wishlisted);
      } else {
        await loadWishState();
      }
      window.dispatchEvent(new Event("wishlist_updated"));
    } catch (err) {
      console.error("Wishlist toggle failed:", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) navigate("/login", { state: { from: productUrl } });
    } finally {
      setTogglingWish(false);
    }
  };

  // ✅ Add to cart via API
  const handleAddToCart = async () => {
    if (!id) return;
    const token = getCustomerToken();
    if (!token) { navigate("/login", { state: { from: productUrl } }); return; }

    try {
      setAdding(true);
      await CartServices.add({
        product_id: id,
        product_info_id: productInfoId ?? null,
        quantity: 1,
        location_product_price_id: locationProductPriceId ?? null, // ✅ pass directly
      });
      window.dispatchEvent(new Event("cart_updated"));
    } catch (err) {
      console.error("Add to cart failed:", err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) navigate("/login", { state: { from: productUrl } });
    } finally {
      setAdding(false);
    }
  };

  const Card = (
    <div className="product-card h-100 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2 d-flex flex-column overflow-hidden">
      {/* Wishlist Icon */}
      <button
        type="button"
        aria-label={wishActive ? "Remove from wishlist" : "Add to wishlist"}
        title={wishActive ? "Remove from wishlist" : "Add to wishlist"}
        onClick={handleToggleWishlist}
        disabled={togglingWish}
        className="position-absolute top-0 end-0 me-8 mt-8 bg-white border border-gray-100 rounded-circle flex-center"
        style={{ width: 34, height: 34, zIndex: 3, pointerEvents: "auto", opacity: togglingWish ? 0.8 : 1 }}
      >
        <i
          className={`ph ph-heart text-lg ${wishActive ? "ph-fill" : ""}`}
          style={{ color: wishActive ? "#E11D48" : "#111827" }}
        />
      </button>

      {/* Discount badge */}
      {discountBadgeText && (
        <span
          className="position-absolute top-0 start-0 ms-8 mt-8 px-10 py-6 rounded-pill fw-bold"
          style={{
            background: "rgba(225,29,72,0.12)",
            color: "#E11D48",
            zIndex: 3,
            fontSize: "clamp(10px, 2.5vw, 12px)",
            lineHeight: 1,
          }}
        >
          {discountBadgeText}
        </span>
      )}

      {/* Product Image */}
      <div
        className="product-card__image-container position-relative w-100 overflow-hidden"
        style={{ height: "160px", backgroundColor: "#f9fafb", borderTopLeftRadius: "16px", borderTopRightRadius: "16px" }}
      >
        <Link
          to={productUrl}
          className="w-100 h-100 d-block overflow-hidden"
          style={{ borderTopLeftRadius: "16px", borderTopRightRadius: "16px" }}
        >
          <img
            src={imageSrc}
            alt={title}
            className="product-image w-100 h-100 object-fit-contain p-10"
            style={{ display: "block", borderTopLeftRadius: "16px", borderTopRightRadius: "16px" }}
          />
        </Link>
      </div>

      {/* Content */}
      <div className="product-card__content d-flex flex-column" style={{ flex: "1 0 auto", padding: 10 }}>

        {/* ✅ Dynamic rating row — overlay star technique (same as ProductDetailsOne) */}
        <div className="d-flex align-items-center gap-6 mb-8">
          <div className="d-flex align-items-center" style={{ gap: 2 }}>
            {renderStars(resolvedRating, 14)}
          </div>

          {hasRating ? (
            <>
              <span className="text-gray-700 text-xs fw-semibold" style={{ lineHeight: 1 }}>
                {resolvedRating.toFixed(1)}
              </span>
              <span className="text-gray-400 text-xs" style={{ lineHeight: 1 }}>
                ({resolvedReviewCount})
              </span>
            </>
          ) : (
            <span className="text-gray-400 text-xs" style={{ lineHeight: 1 }}>
              No reviews yet
            </span>
          )}
        </div>

        <h6 className="title text-md fw-semibold mb-10" style={{ height: "44px" }}>
          <Link
            to={productUrl}
            className="link hover-text-main-600 d-block h-100"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Link>
        </h6>

        {/* Price */}
        <div className="product-card__price mb-12">
          {/* Crossed-out old price — only when old > new */}
          {parseMoney(oldPrice) !== null &&
          parseMoney(newPrice) !== null &&
          parseMoney(oldPrice) > parseMoney(newPrice) && (
            <span className="text-gray-400 text-sm fw-semibold text-decoration-line-through me-6">
              {toMoneyText(oldPrice)}
            </span>
          )}

          {/* Current price — always show if present */}
          {parseMoney(newPrice) !== null ? (
            <span className="text-heading text-md fw-bold">
              {toMoneyText(newPrice)}{" "}
              <span className="text-gray-500 fw-normal text-xs">/each</span>
            </span>
          ) : (
            <span className="text-gray-400 text-sm">Price unavailable</span>
          )}
        </div>

        <div className="mt-auto pt-8 border-top border-gray-100">
          {inStock ? (
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={adding}
              className="product-card__cart btn bg-main-600 text-white hover-bg-main-700 w-100 py-10 rounded-8 flex-center gap-6 fw-medium transition-2 text-sm"
              style={{ height: "38px", opacity: adding ? 0.8 : 1 }}
            >
              <i className="ph ph-shopping-cart text-sm" />
              <span>{adding ? "Adding..." : "Add to Cart"}</span>
            </button>
          ) : (
            <button
              disabled
              className="product-card__cart btn bg-gray-100 text-gray-500 w-100 py-10 rounded-8 flex-center gap-6 fw-medium cursor-not-allowed text-sm"
              style={{ height: "38px" }}
            >
              <i className="ph ph-x-circle text-sm" />
              <span>Out of Stock</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (!noGrid) return <div className="col-xxl-2 col-lg-3 col-sm-4 col-6">{Card}</div>;
  return Card;
};

export default ProductCard;